import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Bell } from "lucide-react";

/**
 * Sidebar bell — shows unread @mention count, click opens a dropdown listing recent mentions.
 * Polls /api/notifications every 30s.
 */
export default function NotificationsBell() {
  const navigate = useNavigate();
  const [data, setData] = useState({ items: [], unread_count: 0 });
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const load = async () => {
    try { const { data } = await api.get("/notifications"); setData(data); }
    catch { /* ignore */ }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onClick);
    return () => { window.removeEventListener("keydown", onKey); window.removeEventListener("mousedown", onClick); };
  }, [open]);

  const markAllRead = async () => {
    try { await api.post("/notifications/mark-read", { all: true }); load(); }
    catch { /* ignore */ }
  };

  const openNotif = async (n) => {
    setOpen(false);
    try { await api.post("/notifications/mark-read", { ids: [n.id] }); } catch { /* ignore */ }
    navigate(`/messages?channel=${n.Channel_ID}&message=${n.Message_ID}`);
    load();
  };

  const fmt = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    const mins = Math.floor((Date.now() - d.getTime()) / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m`;
    if (mins < 1440) return `${Math.floor(mins / 60)}h`;
    return `${Math.floor(mins / 1440)}d`;
  };

  return (
    <div ref={ref} className="relative">
      <button
        data-testid="sidebar-notifications-bell"
        onClick={() => setOpen((o) => !o)}
        className="relative w-9 h-9 rounded-lg flex items-center justify-center text-zinc-600 hover:bg-zinc-100"
        title="Mentions"
      >
        <Bell className="w-4 h-4" />
        {data.unread_count > 0 && (
          <span
            data-testid="sidebar-notifications-badge"
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center"
          >
            {data.unread_count > 9 ? "9+" : data.unread_count}
          </span>
        )}
      </button>
      {open && (
        <div
          data-testid="sidebar-notifications-dropdown"
          className="absolute right-0 mt-2 w-80 bg-white border border-zinc-200 rounded-xl shadow-2xl z-50 overflow-hidden"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-100">
            <div className="font-display font-semibold text-sm">Mentions</div>
            {data.unread_count > 0 && (
              <button
                data-testid="notifications-mark-all-read"
                onClick={markAllRead}
                className="text-xs text-brand-700 hover:underline"
              >
                Mark all read
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {data.items.length === 0 ? (
              <div className="text-center text-xs text-zinc-400 py-10">No mentions yet — quiet on the floor.</div>
            ) : (
              data.items.map((n) => (
                <button
                  key={n.id}
                  data-testid={`notification-${n.id}`}
                  onClick={() => openNotif(n)}
                  className={`w-full text-left px-4 py-3 border-b border-zinc-50 hover:bg-zinc-50 transition-colors ${!n.Read_At ? "bg-brand-50/30" : ""}`}
                >
                  <div className="flex items-baseline justify-between gap-2 mb-1">
                    <span className="text-xs font-semibold text-zinc-900 truncate">{n.From_User_Name}</span>
                    <span className="text-[10px] text-zinc-400 flex-shrink-0">{fmt(n.created_date)}</span>
                  </div>
                  <div className="text-[10px] uppercase tracking-wider text-brand-700 mb-1">#{n.Channel_Name}</div>
                  <div className="text-xs text-zinc-600 line-clamp-2">{n.Snippet}</div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
